import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import transportTheme from "./styles/theme";
import { AppProvider, useNotifications } from "./store/AppContext";
import { NavigationBar } from "./components/layout/NavigationBar";
import { I18nProvider } from "./i18n";

// Pages
import HomePage from "./pages/HomePage";
import FaqPage from "./pages/FaqPage";
import ListingsPage from "./pages/ListingsPage";
import ListingDetailPage from "./pages/ListingDetailPage";
import TransportCompaniesPage from "./pages/TransportCompaniesPage";
import TransportCompanyDetailPage from "./pages/TransportCompanyDetailPage";
import { PilotsPage, PilotDetailPage } from "./pages/PilotsPages";
import { TermsOfServicePage, PrivacyPolicyPage } from "./pages/ContentPages";
import {
  CompaniesHubPage,
  ContactPage,
  LoginPage,
  NotFoundPage,
} from "./pages/OtherPages";
import RegisterPage from "./pages/RegisterPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import {
  FirstStepsPage,
  CompanyOnboardingPage,
  PilotOnboardingPage,
} from "./pages/OnboardingPages";
import {
  AccountDashboardPage,
  MyListingsPage,
  AddListingPage,
  EditListingPage,
  FavouritesPage,
  MessagesPage,
  AccountSettingsPage,
  SentApplicationsPage,
  MyOrdersPage,
  CompanyListingsOffersPage,
  ReceivedApplicationsPage,
  OrdersPage,
  PilotPreferencesPage,
  EditProfilePage,
} from "./pages/AccountPages";
import DinosaurGamePage from "./pages/DinosaurGamePage";

// ============================================================
// GLOBAL NOTIFICATION SNACKBAR
// ============================================================

function GlobalNotificationSnackbar() {
  const { message, severity, isVisible, hideNotification } = useNotifications();

  return (
    <Snackbar
      open={isVisible}
      autoHideDuration={5000}
      onClose={hideNotification}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={hideNotification}
        severity={severity}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
}

// ============================================================
// APP ROUTES
// ============================================================

function AppRoutes() {
  return (
    <NavigationBar>
      <Routes>
        {/* Main routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Listings */}
        <Route path="/listings" element={<ListingsPage />} />
        <Route path="/listings/:listingId" element={<ListingDetailPage />} />
        <Route
          path="/listings/transport-companies"
          element={<TransportCompaniesPage />}
        />
        <Route
          path="/listings/transport-companies/:companyId"
          element={<TransportCompanyDetailPage />}
        />
        <Route path="/listings/pilots" element={<PilotsPage />} />
        <Route path="/listings/pilots/:pilotId" element={<PilotDetailPage />} />

        {/* Companies catalog */}
        <Route path="/companies" element={<CompaniesHubPage />} />
        <Route
          path="/companies/transport-companies"
          element={<TransportCompaniesPage />}
        />
        <Route
          path="/companies/transport-companies/:companyId"
          element={<TransportCompanyDetailPage />}
        />
        <Route path="/companies/pilots" element={<PilotsPage />} />
        <Route
          path="/companies/pilots/:pilotId"
          element={<PilotDetailPage />}
        />

        {/* ── Account ── */}
        <Route path="/account" element={<AccountDashboardPage />} />
        <Route path="/account/settings" element={<AccountSettingsPage />} />
        <Route path="/account/profile" element={<EditProfilePage />} />
        <Route path="/account/preferences" element={<PilotPreferencesPage />} />
        <Route path="/account/my-listings" element={<MyListingsPage />} />
        <Route path="/account/my-listings/add" element={<AddListingPage />} />
        <Route
          path="/account/my-listings/edit/:listingId"
          element={<EditListingPage />}
        />
        <Route path="/account/messages" element={<MessagesPage />} />
        <Route path="/account/favourites" element={<FavouritesPage />} />
        <Route
          path="/account/listings-and-offers"
          element={<CompanyListingsOffersPage />}
        />
        <Route
          path="/account/sent-applications"
          element={<SentApplicationsPage />}
        />
        <Route path="/account/my-orders" element={<MyOrdersPage />} />
        <Route
          path="/account/received-applications"
          element={<ReceivedApplicationsPage />}
        />
        <Route
          path="/account/received-applications/:listingId"
          element={<ReceivedApplicationsPage />}
        />
        <Route path="/account/orders" element={<OrdersPage />} />
        <Route path="/account/orders/:listingId" element={<OrdersPage />} />

        {/* Mini games */}
        <Route path="/dinozaur" element={<DinosaurGamePage />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/logout" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          path="/connect/:provider/redirect"
          element={<AuthCallbackPage />}
        />
        <Route path="/first-steps" element={<FirstStepsPage />} />
        <Route path="/onboarding/company" element={<CompanyOnboardingPage />} />
        <Route path="/onboarding/pilot" element={<PilotOnboardingPage />} />

        {/* Legal */}
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* 404 */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </NavigationBar>
  );
}

// ============================================================
// ROOT APP
// ============================================================

function normalizeOAuthCallbackUrlForHashRouter() {
  const callbackMatch = window.location.pathname.match(
    /^\/connect\/([^/]+)\/redirect\/?$/,
  );
  const isRootGoogleCallback =
    window.location.pathname === "/" &&
    !window.location.hash &&
    new URLSearchParams(window.location.search).has("access_token");

  if ((!callbackMatch && !isRootGoogleCallback) || window.location.hash) return;

  const provider = callbackMatch?.[1] ?? "google";
  const normalizedUrl = `${window.location.origin}/#/connect/${provider}/redirect${window.location.search}`;
  window.history.replaceState(null, "", normalizedUrl);
}

export default function App() {
  normalizeOAuthCallbackUrlForHashRouter();

  return (
    <ThemeProvider theme={transportTheme}>
      <CssBaseline />
      <I18nProvider>
        <AppProvider>
          <HashRouter>
            <AppRoutes />
            <GlobalNotificationSnackbar />
          </HashRouter>
        </AppProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
